import { Button, Checkbox } from "~/components/ui";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { DownloadIcon, PenIcon, TrashIcon } from "lucide-react";
import { DeleteConfirm } from "~/components/common/confirm";
import type { TableActionEventType } from "~/types/ui";

export const defaultTableColumns: { [key: string]: ColumnDef<any> } = {
  select: {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
      />
    ),

    cell: ({ row, table }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onClick={(e) => {
          const anchorRef = table.options.meta?.selectionAnchorRef;
          const anchorId = anchorRef?.current;

          if (e.shiftKey && anchorId) {
            const rows = table.getRowModel().rows;
            const anchorIndex = rows.findIndex((r) => r.id === anchorId);
            const currentIndex = rows.findIndex((r) => r.id === row.id);

            if (anchorIndex !== -1 && currentIndex !== -1) {
              const [start, end] =
                anchorIndex < currentIndex
                  ? [anchorIndex, currentIndex]
                  : [currentIndex, anchorIndex];
              const rangeSelection: Record<string, boolean> = {};
              for (let i = start; i <= end; i++) {
                rangeSelection[rows[i].id] = true;
              }
              table.setRowSelection(rangeSelection);
            }
          } else {
            row.toggleSelected(!row.getIsSelected());
            if (anchorRef) anchorRef.current = row.id;
          }
        }}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
};

export function getActionColDefs(
  callback: (type: TableActionEventType, row: Row<any>) => void,
  permittedEdit: boolean,
  permittedDelete: boolean,
  noDeleteConfirm?: boolean,
  permittedDownload?: boolean,
): ColumnDef<any>[] {
  if (!permittedEdit && !permittedDelete && !permittedDownload)
    return [] as ColumnDef<any>[];
  return [
    {
      id: "actions",
      header: "",
      cell: ({ row }: any) => (
        <div className="flex gap-2 mx-2">
          {permittedDownload && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                callback("download", row);
              }}
            >
              <DownloadIcon className="h-4 w-4" />
            </Button>
          )}
          {permittedEdit && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                callback("edit", row);
              }}
            >
              <PenIcon className="h-4 w-4" />
            </Button>
          )}
          {permittedDelete &&
            (noDeleteConfirm ? (
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => callback("delete", row)}
              >
                <TrashIcon className="h-4 w-4" color="red" />
              </Button>
            ) : (
              <DeleteConfirm
                trigger={
                  <Button variant="ghost" size="icon" type="button">
                    <TrashIcon className="h-4 w-4" color="red" />
                  </Button>
                }
                title={"common.deleteConfirmTitle"}
                description={"common.deleteConfirmDescription"}
                onConfirm={() => callback("delete", row)}
              />
            ))}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
